import React, { useEffect, useMemo } from "react";
import { DatePicker, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import moment from "moment-timezone";
import { dayjsFormat } from "../../../../helpers/utils";
import { getBusinessTimeZone } from "../../../../helpers/moment";
import { svg } from "../../../../Assets/svg";
import {
  DeliveryStatusArray,
  PoductStartEditArray,
  InstallationStatusArray,
} from "../orderTypes";

dayjs.extend(isoWeek);

interface Props {
  values: any;
  setFieldValue: (key: string, value: any) => void;
  errors: any;
  touched: any;
  orderDetails: any;
}

const parseDateToMoment = (dateVal: any, timeZone: string) => {
  if (!dateVal) return null;
  if (typeof dateVal?.toDate === "function") {
    return moment(dateVal.toDate()).tz(timeZone);
  }
  if (dateVal instanceof Date) {
    return moment(dateVal).tz(timeZone);
  }
  return moment(dateVal).tz(timeZone);
};

const PlanningHeaderForm: React.FC<Props> = ({
  values,
  setFieldValue,
  errors,
  touched,
  orderDetails,
}) => {
  const tz = useMemo(() => getBusinessTimeZone(), []);

  // ✅ Helper to disable dates before today
  const disabledDate = (current: Dayjs) => {
    // Can not select days before today
    return current && current < dayjs().startOf("day");
  };

  const currentWeekNumber = useMemo(() => {
    const m = moment().tz(tz);
    return m.isValid() ? m.isoWeek() : dayjs().isoWeek();
  }, [tz]);

  // Set current week as default if not selected
  useEffect(() => {
    if (!values?.production_start_date) {
      const startOfBizWeek = moment().tz(tz).startOf("isoWeek").hour(12).minute(0).second(0);
      setFieldValue("production_start_date", dayjs(startOfBizWeek.toDate()));
    }
  }, [tz]);

  const weekOptions = useMemo(() => {
    return Array.from({ length: 52 }, (_, i) => ({
      value: i + 1,
      label: `Week ${i + 1}`,
    }));
  }, []);

  const handleWeekChange = (selectedWeek: number | null | undefined) => {
    const bizTz = getBusinessTimeZone();
    if (!selectedWeek) {
      const startOfBizWeek = moment().tz(bizTz).startOf("isoWeek").hour(12).minute(0).second(0);
      setFieldValue("production_start_date", dayjs(startOfBizWeek.toDate()));
      return;
    }

    const todayInBiz = moment().tz(bizTz);
    const currentWeek = todayInBiz.isoWeek();
    const currentYear = todayInBiz.year();

    const targetYear =
      selectedWeek >= currentWeek ? currentYear : currentYear + 1;
    const targetDate = moment()
      .tz(bizTz)
      .year(targetYear)
      .isoWeek(selectedWeek)
      .startOf("isoWeek")
      .hour(12)
      .minute(0)
      .second(0);

    setFieldValue("production_start_date", dayjs(targetDate.toDate()));
  };

  const selectedWeekValue = useMemo(() => {
    if (values?.production_start_date) {
      const m = parseDateToMoment(values.production_start_date, tz);
      if (m && m.isValid()) {
        return m.isoWeek();
      }
    }
    return currentWeekNumber;
  }, [values?.production_start_date, tz, currentWeekNumber]);

  return (
    <div className="row g-3 page-form">
      {/* Production Start Week */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">Production Start Week</label>
        <Select
          className="w-100"
          size="large"
          placeholder="Select Production Start Week"
          value={selectedWeekValue}
          onChange={handleWeekChange}
          disabled={
            !PoductStartEditArray?.includes(orderDetails?.order_status)
          }
          options={weekOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <div className="text-muted fs-8 mt-1">
          Current Week: Week {currentWeekNumber}
        </div>
        {errors?.production_start_date && touched?.production_start_date && (
          <div className="text-danger fs-8">
            {errors?.production_start_date}
          </div>
        )}
      </div>

      {/* Production Completion Date */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">
          Production Completion Date
        </label>
        <DatePicker
          suffixIcon={svg?.calenderIcon}
          className="w-100"
          size="large"
          format={dayjsFormat}
          placeholder="Select Production Completion Date"
          value={
            values?.production_completion_date
              ? dayjs(values.production_completion_date)
              : null
          }
          disabled={
            !PoductStartEditArray?.includes(orderDetails?.order_status)
              ? true
              : false
          }
          onChange={(date) =>
            setFieldValue("production_completion_date", date ? date : null)
          }
          // disabled={disabled}
          disabledDate={disabledDate}
        />
        {errors?.production_completion_date &&
          touched?.production_completion_date && (
            <div className="text-danger fs-8">
              {errors?.production_completion_date}
            </div>
          )}
      </div>

      {/* Delivery Date */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">Delivery Date</label>
        <DatePicker
          suffixIcon={svg?.calenderIcon}
          className="w-100"
          size="large"
          format={dayjsFormat}
          placeholder="Select Delivery Date"
          value={values?.delivery_date ? dayjs(values.delivery_date) : null}
          onChange={(date) =>
            setFieldValue("delivery_date", date ? date : null)
          }
          disabled={
            !values.production_completion_date ||
            !values.production_completion_date ||
            !DeliveryStatusArray?.includes(orderDetails?.order_status)
          }
          disabledDate={disabledDate}
        />
        {errors?.delivery_date && touched?.delivery_date && (
          <div className="text-danger fs-8">{errors?.delivery_date}</div>
        )}
      </div>

      {/* Installation Date */}
      {values?.installation_planning ? (
        <div className="col-lg-3 col-md-6 col-sm-6">
          <label className="custom-label mb-1 ff">Installation Date</label>
          <DatePicker
            suffixIcon={svg?.calenderIcon}
            className="w-100"
            size="large"
            format={dayjsFormat}
            placeholder="Select Installation Date"
            value={
              values?.installation_date ? dayjs(values.installation_date) : null
            }
            onChange={(date) =>
              setFieldValue("installation_date", date ? date : null)
            }
            disabled={
              !values.delivery_date ||
              !InstallationStatusArray?.includes(orderDetails?.order_status)
            }
            disabledDate={disabledDate}
          />
          {errors?.installation_date && touched?.installation_date && (
            <div className="text-danger fs-8">{errors?.installation_date}</div>
          )}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default PlanningHeaderForm;
